import { useTranslation } from 'react-i18next';
import Webcam from 'react-webcam';
import { AxiosResponse } from 'axios';
import { useNavigate } from 'react-router-dom';
import { Alert, App, Col, Row } from 'antd';
import { useEffect, useState, useCallback, useRef } from 'react';
import { Routes } from '@/routes';
import { useRefresh, useModal, useFaceErrorFn, useFaceEnrollmentModule } from '@/hooks';
import { DataMoonHttp, UserHttp } from '@/services';
import { blobUrlToFile } from '@/utils';
import { useUniversityContext, useUserContext } from '@/context';
import { AppButton, AppModal, Flex, VideoCapture } from '@/components';
import FaceEnrollmentImage from './FaceEnrollmentImage';
import ConfirmImage from './ConfirmImage';

const FaceEnrollment = () => {

  const [t] = useTranslation(['common', 'face']);
  const { message } = App.useApp();
  const isSupported = 'mediaDevices' in window.navigator;
  const modal = useModal();
  const navigate = useNavigate();
  const { visible: moduleVisible } = useFaceEnrollmentModule();
  const { profile, refresh: refreshProfile } = useUserContext();
  const [refreshCounter, refresh] = useRefresh();
  const [isCapture, setCapture] = useState(false);
  const [permissionError, setPermissionError] = useState(false);
  const [uploading, setUploading] = useState(false);
  const contentWidth = { maxWidth: 'min(90%, 600px)' };
  const parseFaceError = useFaceErrorFn();

  const webcamRef = useRef<Webcam>(null);

  const samad = useUniversityContext()?.university?.samad;

  useEffect(() => {
    if (!moduleVisible) {
      navigate({ pathname: Routes.Home }, { replace: true });
    }
  }, []);

  useEffect(() => {
    if (!isCapture) return;
    navigator.mediaDevices.getUserMedia({ video: { width: 1280, height: 720 } })
      .then(() => {
        if (webcamRef.current?.render) {
          webcamRef.current.render();
        }
      })
      .catch(e => {
        if (e.name === 'NotAllowedError') {
          setPermissionError(true);
        }
      });

    return () => {
      if (webcamRef.current?.video) {
        webcamRef?.current?.video?.pause();
      }
    };
  }, [refreshCounter, isCapture]);

  const handleRetry = useCallback(() => {
    setPermissionError(false);
    setTimeout(refresh, 700);
  }, [refresh, setPermissionError]);

  const handleFileUpload = useCallback(async (mediaBloblURL: string) => {
    setUploading(true);

    const file = await blobUrlToFile(mediaBloblURL);
    try {
      if (!['video', 'image'].includes(samad.faceEnrollmentType)) {
        message.error(t('face:errors.invalid-auth-type'));
        return;
      }

      let response: AxiosResponse;
      if (samad.faceEnrollmentType === 'video') {
        response = await DataMoonHttp.enrollVideo(profile?.username, file);
      }
      else {
        response = await DataMoonHttp.enroll(profile?.username, file);

        await UserHttp.uploadProfilePicture({
          username: profile?.username,
          userImageBase64: mediaBloblURL,
        });
      }

      if (response?.data?.success) {

        if (samad.faceEnrollmentType === 'image') {
          await UserHttp.uploadProfilePicture({
            username: profile?.username,
            userImageBase64: mediaBloblURL,
          });
        }

        message.success(t('face:messages.success-face-enrollment'));
        navigate(Routes.Home);
        refreshProfile();
      }
      else {
        message.error(parseFaceError());
      }
    }
    catch (e) {
      message.error({ duration: 4.5, content: parseFaceError(e?.response?.data) });
    }
    finally {
      setUploading(false);
    }
  }, [message, navigate, profile?.username, refreshProfile, samad.faceEnrollmentType, t, parseFaceError]);

  if (!profile.changeProfilePicturePermission) {
    return (
      <Flex align="center" className="h-full mx-auto my-auto">
        <Alert type="error" message={(
          <div>
            {t('face:errors.capture-profile-permission')}
          </div>
        )} />
      </Flex>
    );
  }

  return (
    <Flex direction="column" className="mx-auto h-full justify-center pb-8">
      <AppModal
        styles={{ body: { maxHeight: 'auto' } }}
        destroyOnClose
        footer={null}
        variant="glass"
        onCancel={modal.hide}
        open={modal.open}
        title={t('face:messages.confirm-image')}
      >
        <ConfirmImage hide={modal.hide} webcam={webcamRef} />
      </AppModal>
      {!isCapture ?
        <>
          <p className="mx-auto text-justify" style={contentWidth}>
            {t('face:messages.face-enroll-first-hint')}
          </p>
          <Row>
            <Col className="mx-auto" xl={4} lg={6} md={10} span={24}>
              <AppButton onClick={() => setCapture(true)} className="w-full" appVariant="submit">
                {t('common:labels.start')}
              </AppButton>
            </Col>
          </Row>
        </> :
        (
          permissionError ? (
            <Row>
              <Col xl={16} lg={20} span={24} className="mx-auto">
                <Alert type="error" message={(
                  <Flex direction="row" gap="0">
                    <p>
                      {t('face:errors.you-block-camera')}
                      <span onClick={handleRetry} className="cursor-pointer text-red-800">{t('common:labels.try-again')}</span>.
                    </p>
                  </Flex>
                )} />
              </Col>
            </Row>
          ) :
            isSupported ?
              <Flex direction="column" className="mx-auto" justify="center" align="center">
                <p>{t('face:messages.ensure-lighting')}</p>
                {samad.faceEnrollmentType !== 'video' ?
                  <VideoCapture
                    onApply={handleFileUpload}
                    sendButtonProps={{ loading: uploading }}
                  /> :
                  <FaceEnrollmentImage
                    uploading={uploading}
                    handleUpload={handleFileUpload}
                  />
                }
              </Flex> :
              <Flex direction="column" className="mx-auto" justify="center" align="center">
                <Row>
                  <Col xl={16} lg={20} span={24} className="mx-auto">
                    <Alert type="error" message={t('face:errors.capture-support')} />
                  </Col>
                </Row>
              </Flex>
        )
      }
    </Flex >
  );
};

export default FaceEnrollment;
